import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { ShipsStructure } from '../../reportsTypes';
import { MultiShipCommand } from '../../../utils/ShipCommand';
import { ReportCloneValues } from '../utils/createReportCloneFormSchema';

interface ShipFormFieldProps {
  form: UseFormReturn<ReportCloneValues>;
  shipsConfig: ShipsStructure | null | undefined;
}

export const ShipFormField: React.FC<ShipFormFieldProps> = ({
  form,
  shipsConfig,
}) => {
  const { messages } = useLocalization();

  // Don't render if no ships config
  if (!shipsConfig) {
    return null;
  }

  const showAttributeSelection = shipsConfig.colorBy && shipsConfig.filterBy;

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="ships"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <div className="flex items-center justify-between">
              <FormLabel>
                {messages.reportsCreateReportShipLabel || 'Vessel:'}
              </FormLabel>
              {showAttributeSelection && (
                <FormField
                  control={form.control}
                  name="shipAttribute"
                  render={({ field: attrField }) => {
                    const hasVessels = Array.isArray(field.value) && field.value.length > 0;
                    return (
                      <div className="flex items-center gap-4">
                        <RadioGroup
                          value={attrField.value as string}
                          onValueChange={attrField.onChange}
                          className="flex items-center gap-4"
                          disabled={!hasVessels}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="filterBy" id="clone-filterBy" />
                            <Label htmlFor="clone-filterBy" className={`text-sm ${hasVessels ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                              Filter by
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="colorBy" id="clone-colorBy" />
                            <Label htmlFor="clone-colorBy" className={`text-sm ${hasVessels ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                              Highlight
                            </Label>
                          </div>
                        </RadioGroup>
                        <InfoIconTooltip>
                          {!hasVessels
                            ? 'Select at least one vessel to enable this option'
                            : attrField.value === 'colorBy'
                              ? 'Highlights chosen vessels by giving them a different color'
                              : 'Only shows the vessels that were chosen'}
                        </InfoIconTooltip>
                      </div>
                    );
                  }}
                />
              )}
            </div>
            <FormControl>
              <MultiShipCommand
                value={Array.isArray(field.value) ? field.value : []}
                onChange={field.onChange}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
